import React, { FC, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Store } from '@/redux';
import {
  describeTurn,
  SUIT_GLYPH,
  TurnDescription,
} from '@/screens/guest/lib/turn';
import { SocketConnectionState } from '../../use-websocket';
import '@/styles/sunday-table.css';

export const useTurnDescription = (): TurnDescription => {
  const { players, gameInfo } = useSelector(
    (state: Store) => state.inPlayReducer
  );
  const userID = useSelector((state: Store) => state.userReducer.id);

  return useMemo(
    () =>
      describeTurn({
        userID,
        room: gameInfo?.room,
        inPlayCards: gameInfo?.inPlayCards,
        userDeck: gameInfo?.userDeck,
        players,
      }),
    [userID, gameInfo, players]
  );
};

/** Full sentence used for the tooltip and the screen-reader announcement. */
export const turnAnnouncement = (
  turn: Pick<TurnDescription, 'title' | 'detail'>
) => (turn.detail ? `${turn.title}. ${turn.detail}` : turn.title);

/**
 * Split the title so only the player name truncates: "A very long name's
 * turn" renders as "A very lo…'s turn" instead of losing the verb.
 */
export const splitTurnTitle = (
  turn: Pick<TurnDescription, 'title' | 'actor'>
): { name: string; rest: string } | null => {
  const { title, actor } = turn;
  if (!actor || !title.startsWith(actor)) return null;
  return { name: actor, rest: title.slice(actor.length) };
};

export interface TurnBannerProps {
  compact?: boolean;
  connectionState?: SocketConnectionState;
  onReconnect?: () => void;
}

/**
 * Fixed-height, single-line "what should I do now" strip: whose turn and
 * which suit leads. The longer explanation never changes the banner's height:
 * it is announced to screen readers, exposed as the line's tooltip and shown
 * on demand in an overlay via the info button.
 */
export const TurnBanner: FC<TurnBannerProps> = ({
  compact = false,
  connectionState = 'open',
  onReconnect,
}) => {
  const turn = useTurnDescription();
  const offline = connectionState !== 'open';
  const [showDetail, setShowDetail] = useState(false);
  const detailId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const announcement = turnAnnouncement(turn);
  const split = splitTurnTitle(turn);

  // Close the details overlay on Escape or a tap elsewhere.
  useEffect(() => {
    if (!showDetail) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowDetail(false);
    };
    const onPointer = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setShowDetail(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [showDetail]);

  const hasDetail = !!turn.detail;

  return (
    <div
      ref={rootRef}
      className={`st-turn st-turn--${turn.tone} ${compact ? 'st-turn--compact' : ''}`}
      data-testid="turn-banner"
      data-tone={turn.tone}
    >
      {/* The suit slot is always reserved so the line never jumps sideways. */}
      <span
        className={`st-turn__suit ${turn.leadSuit ? `st-suit--${turn.leadSuit}` : 'st-turn__suit--empty'}`}
        aria-hidden="true"
        data-testid="turn-banner-suit"
      >
        {turn.leadSuit ? SUIT_GLYPH[turn.leadSuit] : ''}
      </span>

      <p className="st-turn__line" title={announcement} aria-hidden="true">
        {split ? (
          <>
            <span className="st-turn__name">{split.name}</span>
            <span className="st-turn__rest">{split.rest}</span>
          </>
        ) : (
          <span className="st-turn__name">{turn.title}</span>
        )}
      </p>
      <span className="st-sr-only" role="status" aria-live="polite">
        {announcement}
      </span>

      <button
        type="button"
        className="st-turn__info"
        aria-label="Turn details"
        aria-expanded={showDetail}
        aria-controls={detailId}
        disabled={!hasDetail}
        data-hidden={!hasDetail || undefined}
        onClick={() => setShowDetail(v => !v)}
      >
        <span aria-hidden="true">i</span>
      </button>

      {offline && (
        <div className="st-turn__conn" role="alert">
          <span className="st-dot st-dot--warn" aria-hidden="true" />
          {connectionState === 'failed' ? (
            <>
              <span>Disconnected</span>
              {onReconnect && (
                <button
                  type="button"
                  className="st-btn st-btn--small st-btn--ghost"
                  onClick={onReconnect}
                >
                  Reconnect
                </button>
              )}
            </>
          ) : (
            <span>
              {connectionState === 'connecting'
                ? 'Connecting…'
                : 'Reconnecting…'}
            </span>
          )}
        </div>
      )}

      {/* Overlay: positioned outside the flow so opening it never shifts the table. */}
      <div
        id={detailId}
        className="st-turn__more"
        hidden={!showDetail || !hasDetail}
        data-testid="turn-banner-detail"
      >
        {turn.detail}
      </div>
    </div>
  );
};
